// src/App.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoginForm from "./components/LoginForm";
import MfaEnroll from "./components/MfaEnroll";
import MobileTabBar from "./components/MobileTabBar";
import VehicleSelector from "./components/VehicleSelector";
import MessageInbox from "./components/MessageInbox";
import SignupForm from "./components/SignupForm";
import VerifyEmail from "./components/VerifyEmail";
import ProfilePage from "./components/ProfilePage";
import ForgotPassword from "./components/ForgotPassword";
import LandingPage from "./components/LandingPage";
import ContactPage from "./components/ContactPage";
import PrePurchaseInspectionPage from "./components/PrePurchaseInspectionPage";
import AutoRepairPage from "./components/AutoRepairPage";
import HowItWorksPage from "./components/HowItWorksPage";
import VehicleInspectionPage from "./components/VehicleInspectionPage";
import FaqPage from "./components/FaqPage";
import PrivacyPage from "./components/PrivacyPage";
import DeleteAccountPage from "./components/DeleteAccountPage";
import ShopPreviewPage from "./components/ShopPreviewPage";
import ShopExplorerPage from "./components/ShopExplorerPage";
import AdminPage from "./components/AdminPage";
import GettingStartedPage from "./components/GettingStartedPage";
import MobileAppPage from "./components/MobileAppPage";
import DemoPage from "./components/DemoPage";
import { syncBrowserPushSubscription } from "./utils/browserPush";
import { unlockNotificationSound, playMessageSound } from "./utils/notificationSound";
import { API_BASE } from "./utils/apiBase";
import { isNativePlatform } from "./utils/platform";
import { syncNativePush, unregisterNativePush, setForegroundPushHandler } from "./utils/nativePush";
import {
  tokenIsExpired,
  shouldRenewToken,
  sessionCheckIntervalMs,
  isRecentlyActive,
} from "./utils/session";
import { isDemoToken, startDemoSession } from "./utils/demoSession";

// Single canonical host (bare apex). nginx 301-redirects www -> apex; canonical URLs here match.
const SITE_ORIGIN = "https://gearcheckauto.com";

// Presence: only count a user "online" if they actually interacted this recently (matches the
// backend PRESENCE_ONLINE_WINDOW_SECONDS). An idle-but-open tab stops heart-beating and goes offline.
const PRESENCE_ACTIVITY_WINDOW_MS = 5 * 60 * 1000;

const PAGE_METADATA = {
  landing: {
    title: "GearCheckAuto | Connect With Auto Repair Shops Near You",
    description:
      "GearCheckAuto helps vehicle owners connect with nearby auto repair shops for repairs, service, diagnostics, and pre-purchase inspections.",
    canonical: `${SITE_ORIGIN}/`,
  },
  signup: {
    title: "Sign Up - Get Repair Quotes From Nearby Shops | GearCheckAuto",
    description:
      "Create a GearCheckAuto account to submit vehicle repair, service, diagnostic, or pre-purchase inspection requests to nearby auto shops.",
    canonical: `${SITE_ORIGIN}/signup`,
  },
  contact: {
    title: "Contact | GearCheckAuto",
    description:
      "Contact GearCheckAuto with questions about vehicle repair requests, shop registration, pre-purchase inspections, or account support.",
    canonical: `${SITE_ORIGIN}/contact`,
  },
  prePurchaseInspection: {
    title: "Pre-Purchase Car Inspection Near You | GearCheckAuto",
    description:
      "Get a pre-purchase inspection before you buy a used car. Send one request to nearby auto repair shops and mobile mechanics, compare quotes, and book with GearCheckAuto.",
    canonical: `${SITE_ORIGIN}/pre-purchase-inspection`,
  },
  howItWorks: {
    title: "How It Works | GearCheckAuto",
    description:
      "See how GearCheckAuto works: describe what your car needs, send one request to several nearby auto repair shops and mobile mechanics, compare quotes and response times, and book.",
    canonical: `${SITE_ORIGIN}/how-it-works`,
  },
  autoRepair: {
    title: "Find Auto Repair Near You | GearCheckAuto",
    description:
      "Get your car repaired without the runaround. Send one request to several nearby auto repair shops and mobile mechanics, compare quotes and response times, and book.",
    canonical: `${SITE_ORIGIN}/auto-repair`,
  },
  vehicleInspection: {
    title: "Vehicle Inspection & Emissions Testing by State | GearCheckAuto",
    description:
      "Smog check, emissions test, E-Check, state inspection, or inspection sticker - learn what vehicle inspections mean in your state and find a nearby shop or mobile mechanic to help you pass with GearCheckAuto.",
    canonical: `${SITE_ORIGIN}/vehicle-inspection`,
  },
  faq: {
    title: "FAQ | GearCheckAuto",
    description:
      "Answers about auto repair requests, pre-purchase inspections, mobile mechanics, and how GearCheckAuto connects vehicle owners with nearby shops.",
    canonical: `${SITE_ORIGIN}/faq`,
  },
  privacy: {
    title: "Privacy Policy | GearCheckAuto",
    description:
      "How GearCheckAuto collects, uses, and shares information for vehicle owners and repair shops using the website and mobile apps.",
    canonical: `${SITE_ORIGIN}/privacy`,
  },
  deleteAccount: {
    title: "Delete your account | GearCheckAuto",
    description:
      "How to delete your GearCheckAuto account and associated data, what gets removed, and how to request deletion by email.",
    canonical: `${SITE_ORIGIN}/delete-account`,
  },
  mobileApp: {
    title: "GearCheckAuto for iPhone and Android - Get the App | GearCheckAuto",
    description:
      "Download the free GearCheckAuto app for iPhone or Android. Send one repair request to several nearby shops and mobile mechanics, compare quotes and response times, and book from your phone.",
    canonical: `${SITE_ORIGIN}/mobile-app`,
  },
  demo: {
    title: "Try the Demo - No Account Needed | GearCheckAuto",
    description:
      "Try GearCheckAuto without signing up. Open the real product with sample shops and conversations, as a vehicle owner or as a repair shop, and click around.",
    canonical: `${SITE_ORIGIN}/demo`,
  },
};

const DEFAULT_METADATA = PAGE_METADATA.landing;

function upsertMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setPageMetadata(metadata) {
  if (typeof document === "undefined") return;
  document.title = metadata.title;
  upsertMeta("name", "description", metadata.description);

  if (metadata.canonical) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", metadata.canonical);
  }

  // Keep social tags in sync per page (index.html ships only the homepage defaults).
  upsertMeta("property", "og:title", metadata.title);
  upsertMeta("property", "og:description", metadata.description);
  upsertMeta("name", "twitter:title", metadata.title);
  upsertMeta("name", "twitter:description", metadata.description);
  if (metadata.canonical) {
    upsertMeta("property", "og:url", metadata.canonical);
  }
}

function formatSubscriptionDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function getShopSubscriptionPromptState(me) {
  if (!me?.is_shop || me?.shop_subscription_whitelisted) {
    return { shouldPrompt: false, reason: "", detail: "" };
  }

  const status = (me?.shop_subscription_status || "").toLowerCase();
  const isSubscriptionActive = !!me?.shop_subscription_active;
  const hasManagedSubscription =
    isSubscriptionActive || ["active", "past_due", "unpaid", "incomplete", "paused"].includes(status);
  if (hasManagedSubscription) {
    return { shouldPrompt: false, reason: "", detail: "" };
  }
  const trialEndsAt = me?.shop_trial_ends_at ? new Date(me.shop_trial_ends_at) : null;
  const trialDateValid = !!trialEndsAt && !Number.isNaN(trialEndsAt.getTime());
  const trialExpired = trialDateValid && trialEndsAt.getTime() <= Date.now();
  const isExpiredStatus = ["canceled", "incomplete_expired"].includes(status) || trialExpired;

  if (isExpiredStatus) {
    const detail = trialDateValid
      ? `Trial expired on ${formatSubscriptionDate(me.shop_trial_ends_at)}.`
      : "Your subscription access has expired.";
    return { shouldPrompt: true, reason: "expired", detail };
  }

  if (status === "trialing") {
    const detail = trialDateValid
      ? `Trial ends on ${formatSubscriptionDate(me.shop_trial_ends_at)}.`
      : "Your account is currently in trial mode.";
    return { shouldPrompt: true, reason: "trial", detail };
  }

  return { shouldPrompt: false, reason: "", detail: "" };
}


function getShopSetupProgress(me) {
  if (!me?.is_shop) return { complete: true, completed: 0, total: 0, nextStep: "", signature: "complete" };
  const hasLocation = [me.street_address, me.city, me.state, me.zip_code].every((v) => String(v || "").trim());
  const hasSpecializations = Array.isArray(me.shop_specializations) && me.shop_specializations.length > 0;
  const hasBusinessLinks = [me.business_website_url, me.business_facebook_url, me.business_youtube_url].some((v) => String(v || "").trim());
  const steps = [
    {
      key: "name-location",
      label: "Confirm shop name and location",
      done: Boolean(String(me.shop_name || "").trim() && hasLocation),
    },
    {
      key: "logo-business-links",
      label: "Add logo and business links",
      done: Boolean(me.shop_logo_url && hasBusinessLinks),
    },
    {
      key: "specializations",
      label: "Add make specializations",
      done: hasSpecializations,
    },
    {
      key: "general-repair",
      label: "Confirm general repair coverage",
      done: Boolean(me.shop_general_repair || hasSpecializations),
    },
    {
      key: "notifications",
      label: "Confirm message notifications",
      done: Boolean(me.message_email_notifications_enabled || me.message_browser_notifications_enabled),
    },
    {
      key: "preview",
      label: "Preview your shop card",
      done: Boolean(me.shop_name && hasLocation && (hasSpecializations || me.shop_general_repair)),
    },
  ];
  const completed = steps.filter((step) => step.done).length;
  const missingKeys = steps.filter((step) => !step.done).map((step) => step.key);
  return {
    complete: completed === steps.length,
    completed,
    total: steps.length,
    nextStep: steps.find((step) => !step.done)?.label || "",
    signature: missingKeys.length ? missingKeys.join("|") : "complete",
  };
}

function getOnboardingReminderStorageKey(userId, reminderId) {
  return userId && reminderId
    ? `gearcheckauto:onboarding-reminder-suppressed:${userId}:${reminderId}`
    : "";
}

function getOnboardingReminderConfig(me) {
  const shopProgress = getShopSetupProgress(me);
  if (me?.is_shop && !shopProgress.complete) {
    return {
      id: "shop-setup",
      role: "shop",
      title: "Welcome to GearCheckAuto - finish your shop setup",
      body: "Complete your shop checklist so owners see an accurate shop card and you do not miss new leads.",
      progressText: `${shopProgress.completed} of ${shopProgress.total} checklist steps complete`,
      nextStep: shopProgress.nextStep,
      continueLabel: "Continue setup",
      targetView: "profile",
      targetSection: "shop-setup",
      signature: shopProgress.signature,
    };
  }
  return null;
}

function OnboardingReminderModal({ config, checked, onCheckedChange, onContinue, onClose }) {
  if (!config) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Setup reminder</div>
        <h2 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{config.title}</h2>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">{config.body}</p>
        <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 dark:bg-blue-950/40 px-3 py-2 text-sm text-blue-950 dark:text-blue-200">
          <div className="font-semibold">{config.progressText}</div>
          {config.nextStep ? <div className="mt-1 text-xs">Next: {config.nextStep}</div> : null}
        </div>
        <label className="mt-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 select-none">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
            checked={checked}
            onChange={(e) => onCheckedChange(e.target.checked)}
          />
          <span>Do not remind me again unless my setup changes.</span>
        </label>
        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700">
            Later
          </button>
          <button type="button" onClick={onContinue} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            {config.continueLabel || "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

function getUnauthedViewFromLocation() {
  const p = (typeof window !== "undefined" ? window.location.pathname : "/") || "/";
  if (p.startsWith("/login")) return "login";
  if (p.startsWith("/signup")) return "signup";
  if (p.startsWith("/verify")) return "verify";
  if (p.startsWith("/forgot")) return "forgot";
  if (p.startsWith("/contact")) return "contact";
  if (p.startsWith("/pre-purchase-inspection")) return "prePurchaseInspection";
  if (p.startsWith("/how-it-works")) return "howItWorks";
  if (p.startsWith("/auto-repair")) return "autoRepair";
  if (p.startsWith("/vehicle-inspection")) return "vehicleInspection";
  if (p.startsWith("/faq")) return "faq";
  if (p.startsWith("/privacy")) return "privacy";
  if (p.startsWith("/delete-account")) return "deleteAccount";
  if (p.startsWith("/mobile-app")) return "mobileApp";
  if (p.startsWith("/demo")) return "demo";
  return "landing";
}

function getAuthedViewFromLocation() {
  const p = (typeof window !== "undefined" ? window.location.pathname : "/") || "/";
  if (p.startsWith("/messages")) return "messages";
  if (p.startsWith("/shop-explorer")) return "shopExplorer";
  if (p.startsWith("/shop-preview")) return "shopPreview";
  if (p.startsWith("/getting-started")) return "gettingStarted";
  if (p.startsWith("/profile")) return "profile";
  return "home";
}

// URL for each authed view - kept in sync with getAuthedViewFromLocation above.
const AUTHED_VIEW_PATHS = {
  home: "/",
  messages: "/messages",
  shopExplorer: "/shop-explorer",
  shopPreview: "/shop-preview",
  gettingStarted: "/getting-started",
  profile: "/profile",
};

function getEmailFromLocation() {
  if (typeof window === "undefined") return "";
  const u = new URL(window.location.href);
  return u.searchParams.get("email") || "";
}

function getUnauthedPath(view, email = "") {
  if (view === "landing") return "/";
  if (view === "login") return "/login";
  if (view === "signup") return "/signup";
  if (view === "contact") return "/contact";
  if (view === "prePurchaseInspection") return "/pre-purchase-inspection";
  if (view === "howItWorks") return "/how-it-works";
  if (view === "autoRepair") return "/auto-repair";
  if (view === "vehicleInspection") return "/vehicle-inspection";
  if (view === "faq") return "/faq";
  if (view === "privacy") return "/privacy";
  if (view === "deleteAccount") return "/delete-account";
  if (view === "mobileApp") return "/mobile-app";
  if (view === "demo") return "/demo";
  if (view === "verify") {
    const q = email ? `?email=${encodeURIComponent(email)}` : "";
    return `/verify${q}`;
  }
  if (view === "forgot") {
    const q = email ? `?email=${encodeURIComponent(email)}` : "";
    return `/forgot${q}`;
  }
  return "/";
}

function App() {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [forceMfaEnroll, setForceMfaEnroll] = useState(false);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(true);
  const [isShopUser, setIsShopUser] = useState(false);
  const [me, setMe] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState("");
  // In-app banner for a push that arrives while the native app is foregrounded.
  const [pushToast, setPushToast] = useState(null);
  const [subscriptionPromptDismissed, setSubscriptionPromptDismissed] = useState(false);
  const [showDismissedPromptReminder, setShowDismissedPromptReminder] = useState(false);
  const [showOnboardingReminder, setShowOnboardingReminder] = useState(false);
  const [onboardingDontRemindAgain, setOnboardingDontRemindAgain] = useState(false);
  const [dismissedOnboardingReminderKey, setDismissedOnboardingReminderKey] = useState("");
  const [profileSectionTarget, setProfileSectionTarget] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [demoSwitching, setDemoSwitching] = useState(false);
  // Read off the TOKEN rather than /users/me, so the banner is right on the first render after
  // login instead of flashing in once the profile fetch resolves.
  const demoMode = useMemo(() => isDemoToken(token), [token]);
  const previousUnreadCountRef = useRef(null);
  // When this device has a web-push subscription, web push handles new-message alerts, so the
  // in-page popup below stands down (avoids double-notifying). Non-push devices keep using it.
  const pushSubscribedRef = useRef(false);

  const initialView = useMemo(() => {
    if (token) return getAuthedViewFromLocation();
    return getUnauthedViewFromLocation();
  }, [token]);

  const [view, setView] = useState(initialView);

  const [pendingEmail, setPendingEmail] = useState(() =>
    !token && (initialView === "verify" || initialView === "forgot")
      ? getEmailFromLocation()
      : ""
  );
  const authExpiredRef = useRef(false);
  const lastActivityRef = useRef(0);
  // One-shot banner shown on the login screen after an expired session logs the user out,
  // so the sudden jump to login is explained instead of looking like a crash.
  const [sessionNotice, setSessionNotice] = useState("");
  // Role hint carried from a landing-page CTA ("Find a Shop" vs "Register Your Shop") into
  // the signup form, so users don't have to pick the account type a second time.
  const [signupRolePreset, setSignupRolePreset] = useState("");

  const handleSessionExpired = useCallback((nextPath = "/login") => {
    if (authExpiredRef.current) return;
    authExpiredRef.current = true;
    // Read the demo flag off the expiring token BEFORE clearing it. A demo session ending is not
    // an error - the visitor never had an account to sign back into, so sending them to the login
    // screen with "please sign in again" would be a dead end.
    const wasDemo = isDemoToken(localStorage.getItem("token"));
    localStorage.removeItem("token");
    setToken(null);
    previousUnreadCountRef.current = null;
    setBrowserNotificationsEnabled(true);
    setIsShopUser(false);
    setMe(null);
    setBillingStatus("");
    setSubscriptionPromptDismissed(false);
    setShowDismissedPromptReminder(false);
    setShowOnboardingReminder(false);
    setOnboardingDontRemindAgain(false);
    setDismissedOnboardingReminderKey("");
    setProfileSectionTarget("");
    setShowLogoutConfirm(false);
    setPendingEmail("");
    if (wasDemo) {
      setSessionNotice("");
      setView("demo");
      window.history.replaceState({}, "", "/demo");
      return;
    }
    setSessionNotice("Your session expired - please sign in again.");
    setView("login");
    window.history.replaceState({}, "", nextPath);
  }, []);

  useEffect(() => {
    const onPop = () => {
      if (token) {
        setView(getAuthedViewFromLocation());
      } else {
        const v = getUnauthedViewFromLocation();
        setView(v);
        if (v === "verify" || v === "forgot") setPendingEmail(getEmailFromLocation());
      }
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [token]);

  // Keep the URL in sync with the authed view. Forward navigations PUSH a history entry so
  // the browser/Android Back button steps back through screens instead of exiting the app;
  // only the first authed navigation (right after login/restore) REPLACES, so Back never
  // returns to the stale /login URL. When the URL already matches (popstate restored it, or
  // a caller like ProfilePage's openShopPreview pushed it), this is a no-op.
  const lastAuthedPathRef = useRef(null);
  useEffect(() => {
    if (!token) {
      lastAuthedPathRef.current = null;
      return;
    }
    const path = AUTHED_VIEW_PATHS[view];
    if (!path) return;
    if (window.location.pathname === path) {
      lastAuthedPathRef.current = path;
      return;
    }
    if (lastAuthedPathRef.current === null) {
      window.history.replaceState({}, "", path);
    } else {
      window.history.pushState({}, "", path);
    }
    lastAuthedPathRef.current = path;
  }, [view, token]);

  useEffect(() => {
    const metadata = !token ? PAGE_METADATA[view] || DEFAULT_METADATA : DEFAULT_METADATA;
    setPageMetadata(metadata);
  }, [token, view]);

  useEffect(() => {
    authExpiredRef.current = false;
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (res.status === 401 || res.status === 403) {
          handleSessionExpired("/login");
          return;
        }
        if (!res.ok || cancelled) return;
        const me = await res.json();
        if (cancelled) return;
        setMe(me);
        // Admins must have MFA: force enrollment on load too (not just on fresh login), so a reload
        // can't get them into the app without it.
        if (me?.is_admin && !me?.mfa_enabled) setForceMfaEnroll(true);
        setIsShopUser(!!me?.is_shop);
        const enabled = me?.message_browser_notifications_enabled ?? true;
        setBrowserNotificationsEnabled(enabled);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [handleSessionExpired, token, view]);

  // Reconcile the browser-push subscription silently - never prompts here (the soft-ask
  // banner and the Profile toggle handle that). Runs on login and when the pref changes.
  useEffect(() => {
    if (!token) return;
    syncBrowserPushSubscription(token, browserNotificationsEnabled).catch(() => {});
    // Native apps use FCM/APNs instead of Web Push; register/de-register the device token
    // in step with the same message-notification preference. No-op on the web.
    syncNativePush(token, browserNotificationsEnabled).catch(() => {});
  }, [token, browserNotificationsEnabled]);

  // Native foreground push: Android shows no tray notification while the app is open, so
  // surface an in-app banner + chime instead. Skipped while the user is already viewing the
  // inbox (MessageInbox handles its own cue there). No-op on the web.
  useEffect(() => {
    setForegroundPushHandler((n) => {
      const viewingInbox = view === "messages" && (typeof document === "undefined" || document.visibilityState === "visible");
      if (viewingInbox) return;
      setPushToast({
        title: (n && n.title) || "New message",
        body: (n && n.body) || "",
        url: (n && n.data && n.data.url) || "/messages",
      });
      try { playMessageSound(); } catch {}
    });
    return () => setForegroundPushHandler(null);
  }, [view]);

  // Auto-dismiss the foreground push banner.
  useEffect(() => {
    if (!pushToast) return undefined;
    const t = setTimeout(() => setPushToast(null), 6000);
    return () => clearTimeout(t);
  }, [pushToast]);

  // Unlock the message-chime audio on the first user gesture (browsers block audio until
  // then), so the inbox-list chime can play even before a thread is opened.
  useEffect(() => {
    const unlock = () => {
      unlockNotificationSound();
      window.removeEventListener("pointerdown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  // Track whether this device has a live push subscription (gates the in-page popup).
  useEffect(() => {
    if (!token || typeof navigator === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      pushSubscribedRef.current = false;
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) pushSubscribedRef.current = !!sub;
      } catch {
        if (!cancelled) pushSubscribedRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, browserNotificationsEnabled]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    let pollTimer = null;

    const pollUnreadThreads = async () => {
      try {
        const res = await fetch(`${API_BASE}/messages/threads?page=1&page_size=20`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (res.status === 401 || res.status === 403) {
          handleSessionExpired("/login");
          return;
        }
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!Array.isArray(data) || cancelled) return;

        const unreadThreads = data.filter((t) => (t.unread_count || 0) > 0);
        const unreadCount = unreadThreads.reduce((sum, t) => sum + (t.unread_count || 0), 0);

        if (previousUnreadCountRef.current === null) {
          previousUnreadCountRef.current = unreadCount;
          return;
        }

        if (
          unreadCount > previousUnreadCountRef.current &&
          !(view === "messages" && document.visibilityState === "visible") &&
          !pushSubscribedRef.current &&
          browserNotificationsEnabled &&
          typeof window !== "undefined" &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          const newest = unreadThreads[0];
          // A work-order status change / change request already sends its own
          // dedicated push notification, so skip the generic "new message" popup
          // for those event messages (matched by their stable content prefix;
          // legacy rows say "Workflow Event:") to avoid double-notifying.
          const newestLastMessage = String(newest?.last_message || "");
          const newestIsWorkOrderEvent =
            newestLastMessage.startsWith("Work Order Event:") ||
            newestLastMessage.startsWith("Workflow Event:");
          if (!newestIsWorkOrderEvent) {
            const title = "New message in GearCheckAuto";
            const body = newest?.opposing_user
              ? `From ${newest.opposing_user}: ${String(newest.last_message || "").slice(0, 120)}`
              : "You have a new unread message.";
            try {
              const n = new Notification(title, { body, icon: "/logo-192.png", badge: "/logo-192.png" });
              n.onclick = () => {
                try {
                  window.focus();
                } catch {}
                setView("messages");
              };
            } catch {}
          }
        }

        previousUnreadCountRef.current = unreadCount;
      } catch {}
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") pollUnreadThreads();
    };

    pollUnreadThreads();
    pollTimer = setInterval(pollUnreadThreads, 15000);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [browserNotificationsEnabled, handleSessionExpired, token, view]);

  // Presence heartbeat: only mark the user "online" while they're genuinely active. The heartbeat
  // fires when the tab is visible AND there's been real interaction recently, so an idle-but-open
  // tab stops beating and the other party sees them go offline (no more "always online").
  useEffect(() => {
    if (!token) return undefined;

    let lastBeatAt = 0;

    const sendHeartbeat = (force) => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (!force && !isRecentlyActive(lastActivityRef.current, now, PRESENCE_ACTIVITY_WINDOW_MS)) return;
      lastBeatAt = now;
      fetch(`${API_BASE}/users/me/heartbeat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      }).catch(() => {});
    };

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      // Resumed after a gap -> beat now so the user flips back to online without waiting for the tick.
      if (Date.now() - lastBeatAt > 60000) sendHeartbeat(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastActivityRef.current = Date.now();
        sendHeartbeat(true);
      }
    };

    const activityEvents = ["pointerdown", "keydown", "mousemove", "scroll", "touchstart", "wheel"];
    activityEvents.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibilityChange);

    lastActivityRef.current = Date.now(); // opening the app with a token counts as activity
    sendHeartbeat(true);
    const timer = setInterval(() => sendHeartbeat(false), 60000);

    return () => {
      clearInterval(timer);
      activityEvents.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [token]);

  // Keep the session alive: while the app is open, renew the token past its halfway point so an
  // active user never gets logged out; log out only when the token has actually expired. (Avoids a
  // multi-day setTimeout, which overflows and fires immediately.)
  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;

    const checkSession = async () => {
      if (cancelled) return;
      if (tokenIsExpired(token)) {
        handleSessionExpired("/login");
        return;
      }
      if (!shouldRenewToken(token)) return;
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json().catch(() => null);
          const next = data && data.access_token;
          if (next) {
            localStorage.setItem("token", next);
            setToken(next); // re-runs this effect with the fresh token
          }
        } else if (res.status === 401 || res.status === 403) {
          handleSessionExpired("/login");
        }
      } catch {
        /* transient network error - try again on the next tick */
      }
    };

    checkSession();
    const interval = setInterval(checkSession, sessionCheckIntervalMs(token));
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkSession();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [handleSessionExpired, token]);

  const goUnauthedView = (nextView, email = "") => {
    setPendingEmail(email || "");
    setView(nextView);
    window.history.pushState({}, "", getUnauthedPath(nextView, email || ""));
  };

  const handleLogin = (accessToken, opts = {}) => {
    if (accessToken) localStorage.setItem("token", accessToken);
    setDismissedOnboardingReminderKey("");
    setProfileSectionTarget("");
    setShowLogoutConfirm(false);
    setSessionNotice("");
    // Admins without MFA are handed a session but must enroll before doing anything else.
    setForceMfaEnroll(!!opts.enrollMfa);
    setToken(accessToken || "");
    // Land on Getting Started after login so new users see how the site works. The shop setup
    // reminder still pops over it on first login (it is driven by user/role, not the active view).
    setView("gettingStarted");
  };

  // "Try the demo" hands us a token minted by the backend, so from here on it is an ordinary
  // login - the session's restrictions are enforced server-side off the token's `demo` claim.
  const handleStartDemo = (accessToken) => {
    handleLogin(accessToken);
  };

  // Swap between this visitor's own two demo accounts. Passing the current token is what keeps
  // it the SAME session - the backend returns the paired account rather than provisioning a new
  // one, so the owner and shop sides are looking at the same conversation.
  const switchDemoRole = async (role) => {
    if (demoSwitching) return;
    setDemoSwitching(true);
    try {
      handleLogin(await startDemoSession(role, token));
    } catch {
      // Nothing to recover: the visitor keeps the session they already have.
    } finally {
      setDemoSwitching(false);
    }
  };

  const handleLogout = () => {
    // Best-effort: drop this device's native push token while the JWT is still valid.
    unregisterNativePush(token).catch(() => {});
    localStorage.removeItem("token");
    setToken(null);
    previousUnreadCountRef.current = null;
    setBrowserNotificationsEnabled(true);
    setIsShopUser(false);
    setMe(null);
    setBillingStatus("");
    setSubscriptionPromptDismissed(false);
    setShowDismissedPromptReminder(false);
    setShowOnboardingReminder(false);
    setOnboardingDontRemindAgain(false);
    setDismissedOnboardingReminderKey("");
    setProfileSectionTarget("");
    setShowLogoutConfirm(false);
    setView("landing");
    setPendingEmail("");
    window.history.replaceState({}, "", "/");
  };

  const promptState = useMemo(() => getShopSubscriptionPromptState(me), [me]);
  // Subscriptions are sold on the web only. Inside the native apps (Capacitor) we never
  // surface the purchase prompt, since the App Store / Play Store forbid steering users
  // to an external (Stripe) checkout for in-app digital subscriptions.
  const showSubscriptionPrompt =
    promptState.shouldPrompt && !subscriptionPromptDismissed && !isNativePlatform();
  const onboardingReminderConfig = useMemo(() => getOnboardingReminderConfig(me), [me]);
  const onboardingReminderStorageKey = getOnboardingReminderStorageKey(me?.id, onboardingReminderConfig?.id);
  const onboardingReminderDismissalKey = onboardingReminderConfig
    ? `${onboardingReminderConfig.id}:${onboardingReminderConfig.signature || "incomplete"}`
    : "";

  useEffect(() => {
    if (me?.is_shop && me?.id && !onboardingReminderConfig) {
      localStorage.removeItem(getOnboardingReminderStorageKey(me.id, "shop-setup"));
    }
  }, [me?.id, me?.is_shop, onboardingReminderConfig]);

  useEffect(() => {
    if (!token || !onboardingReminderConfig || !onboardingReminderStorageKey || !onboardingReminderDismissalKey) {
      setShowOnboardingReminder(false);
      setOnboardingDontRemindAgain(false);
      return;
    }

    const suppressedSignature = localStorage.getItem(onboardingReminderStorageKey);
    if (suppressedSignature === onboardingReminderConfig.signature) {
      setShowOnboardingReminder(false);
      return;
    }
    if (suppressedSignature && suppressedSignature !== onboardingReminderConfig.signature) {
      localStorage.removeItem(onboardingReminderStorageKey);
    }
    if (dismissedOnboardingReminderKey === onboardingReminderDismissalKey) {
      setShowOnboardingReminder(false);
      return;
    }

    setOnboardingDontRemindAgain(false);
    setShowOnboardingReminder(true);
  }, [
    dismissedOnboardingReminderKey,
    onboardingReminderConfig,
    onboardingReminderDismissalKey,
    onboardingReminderStorageKey,
    token,
  ]);

  function closeOnboardingReminder() {
    if (onboardingDontRemindAgain && onboardingReminderStorageKey && onboardingReminderConfig?.signature) {
      localStorage.setItem(onboardingReminderStorageKey, onboardingReminderConfig.signature);
    }
    setDismissedOnboardingReminderKey(onboardingReminderDismissalKey);
    setShowOnboardingReminder(false);
  }

  function continueOnboardingReminder() {
    closeOnboardingReminder();
    const targetView = onboardingReminderConfig?.targetView || "profile";
    setProfileSectionTarget(onboardingReminderConfig?.targetSection || "");
    setView(targetView);
  }

  useEffect(() => {
    if (!showDismissedPromptReminder) return undefined;
    const timer = setTimeout(() => setShowDismissedPromptReminder(false), 7000);
    return () => clearTimeout(timer);
  }, [showDismissedPromptReminder]);

  useEffect(() => {
    if (!billingStatus) return undefined;
    const timer = setTimeout(() => setBillingStatus(""), 7000);
    return () => clearTimeout(timer);
  }, [billingStatus]);

  const activateSubscription = useCallback(async () => {
    // Web-only billing: never initiate an external (Stripe) checkout from the native apps.
    if (isNativePlatform()) return;
    try {
      setBillingLoading(true);
      setBillingStatus("");
      const res = await fetch(`${API_BASE}/users/me/billing/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        handleSessionExpired("/login");
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Unable to start subscription checkout.");
      }
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      setBillingStatus(e?.message || "Unable to start subscription checkout.");
    } finally {
      setBillingLoading(false);
    }
  }, [handleSessionExpired, token]);

  useEffect(() => {
    if (!isShopUser && view === "shopPreview") {
      setView("home");
    }
  }, [isShopUser, view]);

  // Keep non-admins out of the admin view (server enforces too; this is UX).
  useEffect(() => {
    if (view === "admin" && me && !me.is_admin) {
      setView("home");
    }
  }, [view, me]);

  if (!token) {
    if (view === "landing") {
      return (
        <LandingPage
          onGoLogin={() => goUnauthedView("login")}
          onGoSignup={(role) => {
            setSignupRolePreset(role === "shop" || role === "customer" ? role : "");
            goUnauthedView("signup");
          }}
          onGoContact={() => goUnauthedView("contact")}
          onNavigate={(v) => goUnauthedView(v)}
        />
      );
    }

    if (view === "signup") {
      return (
        <div className="p-4">
          <SignupForm
            initialRole={signupRolePreset}
            onGoVerify={(email) => {
              goUnauthedView("verify", email || "");
            }}
          />
        </div>
      );
    }

    if (view === "verify") {
      return (
        <div className="p-4">
          <VerifyEmail
            initialEmail={pendingEmail}
            onVerified={(email) => {
              goUnauthedView("login", email || "");
            }}
          />
        </div>
      );
    }


    if (view === "contact") {
      return <ContactPage onGoBack={() => goUnauthedView("landing")} />;
    }

    if (view === "prePurchaseInspection") {
      return <PrePurchaseInspectionPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "howItWorks") {
      return <HowItWorksPage onNavigate={(v) => goUnauthedView(v)} onTryDemo={handleStartDemo} />;
    }

    if (view === "autoRepair") {
      return <AutoRepairPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "vehicleInspection") {
      return <VehicleInspectionPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "faq") {
      return <FaqPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "privacy") {
      return <PrivacyPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "deleteAccount") {
      return <DeleteAccountPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "mobileApp") {
      return <MobileAppPage onNavigate={(v) => goUnauthedView(v)} />;
    }

    if (view === "demo") {
      return (
        <DemoPage onNavigate={(v) => goUnauthedView(v)} onTryDemo={handleStartDemo} />
      );
    }

    if (view === "forgot") {
      return (
        <div className="p-4">
          <ForgotPassword
            initialEmail={pendingEmail}
            onGoVerify={(email) => {
              goUnauthedView("verify", email || "");
            }}
            onDone={(email) => {
              goUnauthedView("login", email || pendingEmail || "");
            }}
          />
        </div>
      );
    }

    return (
      <div className="p-4">
        <LoginForm
          defaultEmail={pendingEmail}
          notice={sessionNotice}
          onLogin={handleLogin}
          onGoSignup={() => goUnauthedView("signup")}
          onGoVerify={(email) => {
            goUnauthedView("verify", email || "");
          }}
          onGoForgot={(email) => {
            goUnauthedView("forgot", email || "");
          }}
        />
      </div>
    );
  }

  if (forceMfaEnroll) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-gray-900 px-4 py-10 text-gray-900 dark:text-gray-100">
        <div className="mx-auto max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
          <h1 className="text-xl font-bold">Set up two-factor authentication</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Admin accounts are required to use two-factor authentication. Set it up now to continue.
          </p>
          <div className="mt-4">
            <MfaEnroll token={token} mandatory onComplete={() => setForceMfaEnroll(false)} />
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-4 text-sm text-gray-500 dark:text-gray-400 hover:underline"
          >
            Log out instead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 sm:pb-4">
      {pushToast && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            const url = pushToast.url || "/messages";
            setPushToast(null);
            if (url.startsWith("/messages")) setView("messages");
            else { try { window.location.assign(url); } catch {} }
          }}
          className="fixed top-[calc(0.75rem+env(safe-area-inset-top))] inset-x-3 z-[60] mx-auto max-w-md cursor-pointer rounded-xl border border-blue-300 dark:border-blue-800 bg-white dark:bg-gray-800 shadow-lg p-3"
        >
          <div className="flex items-start gap-2">
            <img src="/logo-192.png" alt="" width="24" height="24" className="rounded mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{pushToast.title || "New message"}</div>
              {pushToast.body ? <div className="text-xs text-gray-600 dark:text-gray-300 truncate">{pushToast.body}</div> : null}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={(e) => { e.stopPropagation(); setPushToast(null); }}
              className="ml-auto -mr-1 -mt-1 px-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {showSubscriptionPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl border border-amber-300 dark:border-amber-900 text-amber-900 dark:text-amber-200">
            <div className="font-semibold">Subscription reminder</div>
            <p className="text-sm mt-1">
              {promptState.reason === "trial"
                ? "Your shop account is currently in trial mode."
                : "Your shop subscription has expired."}
            </p>
            <p className="text-sm mt-1">{promptState.detail}</p>
            <p className="text-sm mt-2">Would you like to activate your subscription now?</p>
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={activateSubscription}
                disabled={billingLoading}
                className="px-3 py-1 rounded-xl bg-blue-600 text-white disabled:opacity-50"
              >
                {billingLoading ? "Loading…" : "Yes, activate subscription"}
              </button>
              <button
                onClick={() => {
                  setSubscriptionPromptDismissed(true);
                  setShowDismissedPromptReminder(true);
                }}
                className="px-3 py-1 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
              >
                No, remind me later
              </button>
            </div>
          </div>
        </div>
      )}

      {demoMode && (
        <div className="mb-4 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-900 dark:text-amber-200">
          <p className="font-semibold">You are in the GearCheckAuto demo.</p>
          <p className="mt-1">
            This is your own private demo - nobody else can see it, and it is deleted
            automatically when your session ends. Replies from the other side are generated
            automatically, not written by a real shop. Please do not enter real personal
            information.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                handleLogout();
                goUnauthedView("signup");
              }}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Create a free account
            </button>
            <button
              type="button"
              onClick={() => switchDemoRole(isShopUser ? "owner" : "shop")}
              disabled={demoSwitching}
              className="rounded-lg border border-amber-400 dark:border-amber-700 px-4 py-2 text-sm font-semibold text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60"
            >
              {demoSwitching
                ? "Switching..."
                : isShopUser
                ? "Switch to the vehicle owner view"
                : "Switch to the shop view"}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-amber-900 dark:text-amber-200 underline hover:text-amber-700"
            >
              Exit demo
            </button>
          </div>
        </div>
      )}

      {!showSubscriptionPrompt && promptState.shouldPrompt && showDismissedPromptReminder && !isNativePlatform() && (
        <div className="mb-4 border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200 rounded-xl p-3 text-sm">
          Reminder: You can activate your subscription any time from your Profile page when you're ready.
        </div>
      )}

      {billingStatus ? (
        <div className="mb-4 border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200 rounded-xl p-3 text-sm">
          {billingStatus}
        </div>
      ) : null}

      {/* Desktop nav. On mobile, navigation is the bottom tab bar (MobileTabBar) below. */}
      <div className="mb-5 hidden sm:flex items-center gap-2 rounded-xl border border-blue-300 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/40 p-2 overflow-x-auto">
        {/* Desktop: segmented control */}
        <div className="hidden sm:inline-flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
          {[
            { key: "home", label: "Car Request" },
            { key: "messages", label: "Messages" },
            { key: "shopExplorer", label: "Shop Explorer" },
            ...(isShopUser ? [{ key: "shopPreview", label: "Shop Preview" }] : []),
            { key: "gettingStarted", label: "Getting Started" },
            { key: "profile", label: "Profile" },
            ...(me?.is_admin ? [{ key: "admin", label: "Admin" }] : []),
          ].map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setView(s.key)}
              aria-pressed={view === s.key}
              className={
                "px-3 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap " +
                (view === s.key ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:text-gray-900")
              }
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Log Out (desktop) */}
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="hidden sm:inline-flex bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-sm rounded-xl whitespace-nowrap"
        >
          Log Out
        </button>
      </div>

      <MobileTabBar
        view={view}
        onNavigate={setView}
        isShopUser={isShopUser}
        isAdmin={!!me?.is_admin}
        onLogout={() => setShowLogoutConfirm(true)}
      />

      {showOnboardingReminder && onboardingReminderConfig ? (
        <OnboardingReminderModal
          config={onboardingReminderConfig}
          checked={onboardingDontRemindAgain}
          onCheckedChange={setOnboardingDontRemindAgain}
          onContinue={continueOnboardingReminder}
          onClose={closeOnboardingReminder}
        />
      ) : null}

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">Log out of your account?</p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleLogout}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-check not-italic" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {view === "home" && (
        <VehicleSelector
          token={token}
          onLogout={handleLogout}
          onSessionExpired={handleSessionExpired}
          isShopUser={isShopUser}
          currentUserId={me?.id || ""}
          userZipCode={me?.zip_code || ""}
          demoMode={demoMode}
          onGoToMessages={() => setView("messages")}
        />
      )}
      {view === "messages" && (
        <MessageInbox
          token={token}
          onLogout={handleLogout}
          onSessionExpired={handleSessionExpired}
          prefEnabled={browserNotificationsEnabled}
        />
      )}
      {view === "shopExplorer" && (
        <ShopExplorerPage
          token={token}
          onSessionExpired={handleSessionExpired}
          userZipCode={me?.zip_code || ""}
        />
      )}
      {view === "shopPreview" && <ShopPreviewPage token={token} onSessionExpired={handleSessionExpired} />}
      {view === "gettingStarted" && <GettingStartedPage isShopUser={isShopUser} onNavigate={setView} />}
      {view === "profile" && (
        <ProfilePage
          token={token}
          onLogout={handleLogout}
          onSessionExpired={handleSessionExpired}
          initialSection={profileSectionTarget}
          onInitialSectionApplied={() => setProfileSectionTarget("")}
        />
      )}
      {view === "admin" && me?.is_admin && (
        <AdminPage token={token} onSessionExpired={handleSessionExpired} viewerTimeZone={me?.time_zone || ""} />
      )}
    </div>
  );
}

export default App;
